// src/components/StickyAddToCart.js
// Mobile-only Add to Cart bar fixed to the bottom of product pages.
// Slides in whenever the page's main buttons are off-screen, so buying is
// always one tap away, and hides again when they're back in view so there
// are never two Add to Cart buttons on screen at once.
// Sets --sticky-cta-height CSS variable (0 when hidden or on desktop) so the
// page footer and BackToTop button can move clear of it.

import React, { useEffect, useRef, useState } from 'react';
import { ShoppingBag, Check } from 'lucide-react';

const StickyAddToCart = ({ product, symbol, added, disabled, onAdd, targetRef }) => {
    const barRef = useRef(null);
    const [visible, setVisible] = useState(false);

    // Watch the main buttons rather than scroll position — works whether
    // they're below the fold (long description on a phone) or scrolled past.
    useEffect(() => {
        const target = targetRef.current;
        if (!target) return;
        const observer = new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting));
        observer.observe(target);
        return () => observer.disconnect();
    }, [targetRef]);

    // offsetHeight is 0 while the bar is display:none (lg and up), so the
    // variable stays 0 on desktop without a separate breakpoint check.
    useEffect(() => {
        const height = visible ? barRef.current?.offsetHeight || 0 : 0;
        document.documentElement.style.setProperty('--sticky-cta-height', `${height}px`);
        return () => document.documentElement.style.setProperty('--sticky-cta-height', '0px');
    }, [visible]);

    if (!product.availableForSale) return null;

    return (
        <div
            ref={barRef}
            aria-hidden={!visible}
            className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur border-t border-border shadow-2xl transition-all duration-300 ${
                visible ? 'translate-y-0' : 'translate-y-full invisible'
            }`}
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
            <div className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                    <p className="text-foreground text-sm font-medium tracking-wide truncate">{product.name}</p>
                    <p className="text-[#CEAC8F] text-sm">{symbol}{product.price.toFixed(2)}</p>
                </div>
                <button
                    onClick={onAdd}
                    disabled={disabled}
                    tabIndex={visible ? 0 : -1}
                    className={`flex-shrink-0 flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-all disabled:opacity-60 ${
                        added ? 'bg-green-600 text-white' : 'bg-[#CEAC8F] text-black hover:bg-[#d4b896]'
                    }`}
                >
                    {added ? (
                        <><Check className="w-4 h-4" /> Added</>
                    ) : (
                        <><ShoppingBag className="w-4 h-4" /> Add to Cart</>
                    )}
                </button>
            </div>
        </div>
    );
};

export default StickyAddToCart;
